import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from '../hooks/use-toast';
import { filterProductsByAmount, getBillLabel } from '../mock/mockData';
import { getProducts, getCustomerTransactions, createStripeCheckout, checkPaymentStatus } from '../services/api';
import { ShoppingCart, Search, LogOut, User, Receipt, ShoppingBag, Plus, Minus, Trash2, Settings } from 'lucide-react';
import PaymentMethodModal from './PaymentMethodModal';
import SettingsModal from './SettingsModal';

const CustomerDashboard = () => {
  const { user, logout, cart, addToCart, removeFromCart, updateCartQuantity, clearCart } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchAmount, setSearchAmount] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadProducts();
    loadTransactions();
    
    // Check for payment return
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      checkPayment(sessionId);
    }
  }, [user]);

  const loadProducts = async () => {
    const products = await getProducts();
    setAllProducts(products);
  };

  const loadTransactions = async () => {
    if (user?.id) {
      const trans = await getCustomerTransactions(user.id);
      setTransactions(trans);
    }
  };

  const checkPayment = async (sessionId) => {
    setLoading(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const pollStatus = async () => {
      try {
        const status = await checkPaymentStatus(sessionId);
        
        if (status.confirmationCode) {
          toast({
            title: 'Payment Successful!',
            description: `Confirmation Code: ${status.confirmationCode}`
          });
          await loadTransactions();
          clearCart();
          setLoading(false);
          // Remove session_id from URL
          window.history.replaceState({}, '', '/customer');
          return;
        }
        
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(pollStatus, 2000);
        } else {
          setLoading(false);
          toast({
            title: 'Payment Processing',
            description: 'Your payment is being processed. Check back in a moment.'
          });
        }
      } catch (error) {
        console.error('Payment check error:', error);
        setLoading(false);
      }
    };
    
    pollStatus();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearch = () => {
    const amount = parseFloat(searchAmount);
    if (isNaN(amount) || amount < 1 || amount > 130) {
      toast({
        title: 'Invalid amount',
        description: 'Please enter an amount between $1 and $130',
        variant: 'destructive'
      });
      return;
    }
    
    // Filter products - will show exact match at top
    const matchingProducts = allProducts.filter(product => {
      const diff = Math.abs(product.price - amount);
      return diff <= 5;
    });
    
    // Sort by exact match first
    const sortedProducts = matchingProducts.sort((a, b) => {
      const diffA = Math.abs(a.price - amount);
      const diffB = Math.abs(b.price - amount);
      return diffA - diffB;
    });
    
    setFilteredProducts(sortedProducts);
    
    if (sortedProducts.length === 0) {
      toast({
        title: 'No products found',
        description: `No products found near $${amount}. Try a different amount.`
      });
    } else {
      const exactMatch = sortedProducts[0].price === amount;
      toast({
        title: 'Products found',
        description: exactMatch 
          ? `Found exact match at $${amount}!` 
          : `Found ${sortedProducts.length} products near $${amount}`
      });
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast({
      title: 'Added to cart',
      description: `${product.name} added to your cart`
    });
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast({
        title: 'Cart is empty',
        description: 'Add items to cart before checkout',
        variant: 'destructive'
      });
      return;
    }
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = async (paymentMethod) => {
    try {
      setLoading(true);
      const billLabel = getBillLabel(cart[0].category);
      
      // Create Stripe checkout session
      const checkoutData = {
        amount: cartTotal,
        items: cart.map(item => ({
          productId: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        customerId: user.id,
        customerName: user.username,
        billLabel
      };
      
      const response = await createStripeCheckout(checkoutData);
      
      // Redirect to Stripe checkout page
      window.location.href = response.url;
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: 'Payment Error',
        description: 'Failed to process payment. Please try again.',
        variant: 'destructive'
      });
      setLoading(false);
      setShowPaymentModal(false);
    }
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingBag className="h-8 w-8 text-indigo-600" />
              <h1 className="text-2xl font-bold text-gray-900">DressPay</h1>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => setShowCart(!showCart)}
                className="relative hover-lift"
              >
                <ShoppingCart className="h-5 w-5" />
                {cart.length > 0 && (
                  <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0">
                    {cart.length}
                  </Badge>
                )}
              </Button>
              <Button variant="outline" onClick={() => setShowSettings(true)} className="hover-lift">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium">{user?.username}</span>
              </div>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="shop" className="space-y-6">
          <TabsList>
            <TabsTrigger value="shop">
              <Search className="h-4 w-4 mr-2" />
              Shop
            </TabsTrigger>
            <TabsTrigger value="transactions">
              <Receipt className="h-4 w-4 mr-2" />
              My Orders
            </TabsTrigger>
          </TabsList>

          {/* Shop Tab */}
          <TabsContent value="shop" className="space-y-6">
            {/* Search Section */}
            <Card className="hover-lift">
              <CardHeader>
                <CardTitle>Search by Amount</CardTitle>
                <CardDescription>
                  Enter an amount between $1 and $130 to find products
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label htmlFor="amount">Amount ($)</Label>
                    <Input
                      id="amount"
                      type="number"
                      min="1"
                      max="130"
                      step="0.01"
                      value={searchAmount}
                      onChange={(e) => setSearchAmount(e.target.value)}
                      placeholder="Enter amount (e.g., 45)"
                      className="transition-smooth"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleSearch} className="hover-lift">
                      <Search className="h-4 w-4 mr-2" />
                      Search
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Products Grid */}
            {filteredProducts.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Products near <span className="number-display text-indigo-600">${searchAmount}</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredProducts.map((product) => (
                    <Card key={product.id} className="overflow-hidden hover-lift transition-smooth">
                      <div className="aspect-square overflow-hidden bg-gray-100">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover hover:scale-105 transition-transform"
                        />
                      </div>
                      <CardContent className="p-4">
                        <Badge variant="secondary" className="mb-2">
                          {product.category}
                        </Badge>
                        <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
                        <p className="text-sm text-gray-600 mb-3">{product.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-2xl font-bold text-indigo-600 number-display">
                            ${product.price.toFixed(2)}
                          </span>
                          <Button
                            size="sm"
                            onClick={() => handleAddToCart(product)}
                            className="hover-lift"
                          >
                            Add to Cart
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Category Guide */}
            <Card className="hover-lift">
              <CardHeader>
                <CardTitle>Product Categories by Price</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 border rounded-lg hover:shadow-md transition-smooth">
                    <h3 className="font-semibold text-lg mb-2 number-display">$1 - $20</h3>
                    <p className="text-sm text-gray-600">Fruits & Drinks</p>
                  </div>
                  <div className="p-4 border rounded-lg hover:shadow-md transition-smooth">
                    <h3 className="font-semibold text-lg mb-2 number-display">$20 - $60</h3>
                    <p className="text-sm text-gray-600">Toys & Baby Clothes</p>
                  </div>
                  <div className="p-4 border rounded-lg hover:shadow-md transition-smooth">
                    <h3 className="font-semibold text-lg mb-2 number-display">$60 - $70</h3>
                    <p className="text-sm text-gray-600">Diapers</p>
                  </div>
                  <div className="p-4 border rounded-lg hover:shadow-md transition-smooth">
                    <h3 className="font-semibold text-lg mb-2 number-display">$70 - $130</h3>
                    <p className="text-sm text-gray-600">Men & Women Clothes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Your Orders</CardTitle>
                <CardDescription>View your purchase history with confirmation codes</CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Receipt className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No orders yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {transactions.map((transaction) => (
                      <div key={transaction.id} className="border rounded-lg p-4 hover-lift transition-smooth">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge>{transaction.billLabel}</Badge>
                              <Badge variant="outline" className="number-display">Code: {transaction.confirmationCode}</Badge>
                            </div>
                            <p className="text-sm text-gray-600">
                              {new Date(transaction.date).toLocaleDateString()} at{' '}
                              {new Date(transaction.date).toLocaleTimeString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-indigo-600 number-display">
                              ${transaction.total.toFixed(2)}
                            </p>
                            <p className="text-sm text-gray-600">{transaction.paymentMethod}</p>
                          </div>
                        </div>
                        <div className="space-y-2 bg-gray-50 p-3 rounded">
                          {transaction.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>
                                {item.name} x {item.quantity}
                              </span>
                              <span className="font-medium number-display">
                                ${(item.price * item.quantity).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowCart(false)}>
          <div
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold">Your Cart</h2>
                  <Button variant="ghost" onClick={() => setShowCart(false)}>
                    ✕
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {cart.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ShoppingCart className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                    <p>Your cart is empty</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.id} className="flex gap-4 border rounded-lg p-4">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold">{item.name}</h3>
                          <p className="text-sm text-gray-600 number-display">${item.price.toFixed(2)}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                updateCartQuantity(item.id, Math.max(1, item.quantity - 1))
                              }
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-8 text-center number-display">{item.quantity}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => removeFromCart(item.id)}
                              className="ml-auto"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {cart.length > 0 && (
                <div className="border-t p-6">
                  <div className="flex justify-between mb-4">
                    <span className="text-lg font-semibold">Total:</span>
                    <span className="text-2xl font-bold text-indigo-600 number-display">
                      ${cartTotal.toFixed(2)}
                    </span>
                  </div>
                  <Button onClick={handleCheckout} className="w-full hover-lift" size="lg">
                    Proceed to Checkout
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={handlePaymentConfirm}
        total={cartTotal}
      />
      <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
};

export default CustomerDashboard;